/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { ActionButton, ProgressCircle, TooltipTrigger, Tooltip } from '@adobe/react-spectrum';
import FolderOpen from '@spectrum-icons/workflow/FolderOpen';
import Alert from '@spectrum-icons/workflow/Alert';

import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import PickerDialog from './PickerDialog';
import { useSelectionContext } from './SelectionContext';

import classes from './PickerButton.scss';

const messages = defineMessages({
    statusLoading: {
        id: 'status.loading',
        defaultMessage: 'Loading'
    },
    actionOpenPicker: {
        id: 'action.openPicker',
        defaultMessage: 'Open Picker'
    },
    statusError: {
        id: 'status.error',
        defaultMessage: 'Error'
    }
});

const PickerButton = props => {
    const { disabled, dialogTitle, onClose, onConfirm, testId, position } = props;
    const [schemaStatus, schemaError] = useGraphqlSchemaContext();
    const [{ loading }] = useSelectionContext();
    const { formatMessage } = useIntl();

    const customClasses = {};
    if (position === 'right') {
        customClasses.UNSAFE_className = classes['position_right'];
    }
    if (position === 'center') {
        customClasses.UNSAFE_className = classes['position_center'];
    }

    if (loading || schemaStatus === 'loading') {
        return (
            <ActionButton
                data-test-id={testId}
                aria-label={formatMessage(messages.statusLoading)}
                isDisabled={true}
                {...customClasses}
            >
                <ProgressCircle aria-label={formatMessage(messages.statusLoading)} size="S" isIndeterminate />
            </ActionButton>
        );
    }

    if (schemaStatus === 'done') {
        return (
            <PickerDialog dialogTitle={dialogTitle} onClose={onClose} onConfirm={onConfirm}>
                <ActionButton
                    data-test-id={testId}
                    aria-label={formatMessage(messages.actionOpenPicker)}
                    isDisabled={disabled}
                    {...customClasses}
                >
                    <FolderOpen />
                </ActionButton>
                {props.children}
            </PickerDialog>
        );
    }

    if (schemaStatus === 'error') {
        return (
            <TooltipTrigger placement="left">
                <ActionButton data-test-id={testId} aria-label={formatMessage(messages.statusError)} {...customClasses}>
                    <Alert />
                </ActionButton>
                <Tooltip variant="negative" isOpen>
                    <strong>{schemaError.title}:</strong> {schemaError.description}
                </Tooltip>
            </TooltipTrigger>
        );
    }

    return null;
};

PickerButton.propTypes = {
    disabled: PropTypes.bool,
    dialogTitle: PropTypes.string,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    testId: PropTypes.string,
    position: PropTypes.string
};

export default PickerButton;
